import { useEffect, useState } from 'react';

import {
  BOOST_EM_INSIGHT_TYPE,
  Recommendation,
  useGetBoostEmRecommendationsQuery,
} from 'shared/api/insights';
import { useAssets } from 'shared/api/retail';
import { hasAsset } from 'shared/lib/utils';
import { BOOST_EM_ASSET } from 'shared/config/entitelment-constants';
import { useAllClassesData } from 'entities/classes';
import { localStorageService } from 'shared/lib/local-storage';
import { dateService } from 'shared/lib/date';

import { checkInsightsCacheIsStale } from '../../lib';

export const useBoostEmRecommendations = () => {
  // handle loading state manually to avoid loading spinner flickering
  const [isLoading, setIsLoading] = useState(true);
  const [currentBoostEmRecommendations, setCurrentBoostEmRecommendations] =
    useState<Recommendation[]>();

  const [shouldFetchBoostEmInsights, setShouldFetchBoostEmInsights] = useState<
    boolean | null
  >(null);

  const { allClassesKeys, isClassesLoading } = useAllClassesData();
  const { userAssets } = useAssets();

  const { data: boostEmRecommendations } = useGetBoostEmRecommendationsQuery(
    allClassesKeys,
    {
      skip: !allClassesKeys.length || !shouldFetchBoostEmInsights,
    },
  );

  /*
   * Trigger request to insights-service if user is entitled to required asset and
   * there is no data cached in the local storage.
   * If there is data in the local storage request will be triggered
   * only if cached data is stale (beginning of the next week)
   * */
  useEffect(() => {
    const cachedBoostEmRecommendations = localStorageService.getUserData(
      BOOST_EM_INSIGHT_TYPE,
    );

    if (
      cachedBoostEmRecommendations &&
      'recommendations' in cachedBoostEmRecommendations &&
      'receivedAt' in cachedBoostEmRecommendations
    ) {
      const isCachedDataStale = checkInsightsCacheIsStale(
        cachedBoostEmRecommendations.receivedAt,
      );

      if (isCachedDataStale) {
        setShouldFetchBoostEmInsights(true);
      } else {
        setIsLoading(false);
        setCurrentBoostEmRecommendations(
          cachedBoostEmRecommendations.recommendations,
        );
      }
      return;
    }

    if (!userAssets.length) return;

    const hasBoostEmAsset = hasAsset(BOOST_EM_ASSET, userAssets);

    setIsLoading(hasBoostEmAsset);
    setShouldFetchBoostEmInsights(hasBoostEmAsset);
  }, [userAssets]);

  /*
   * Store Boost EM recommendations and timestamp when they were received in the local storage
   * to prevent requests more than once a week as such recommendations are generated on a weekly basis
   * */
  useEffect(() => {
    if (!boostEmRecommendations?.length) return;

    const cachedBoostEmRecommendations = localStorageService.getUserData(
      BOOST_EM_INSIGHT_TYPE,
    );

    if (
      cachedBoostEmRecommendations &&
      'recommendations' in cachedBoostEmRecommendations &&
      'receivedAt' in cachedBoostEmRecommendations
    ) {
      const isCachedDataStaled = checkInsightsCacheIsStale(
        cachedBoostEmRecommendations.receivedAt,
      );

      // store data in local storage only if there is no data yet or data is stale
      if (
        cachedBoostEmRecommendations.recommendations.length &&
        !isCachedDataStaled
      )
        return;
    }

    const receivedAt = dateService
      .getDayJSObject()
      .tz('America/New_York')
      .valueOf();

    localStorageService.saveUserData(BOOST_EM_INSIGHT_TYPE, {
      recommendations: boostEmRecommendations,
      receivedAt,
    });
    setCurrentBoostEmRecommendations(boostEmRecommendations);
  }, [boostEmRecommendations]);

  useEffect(() => {
    // if there is no data an empty array will be received
    if (shouldFetchBoostEmInsights && boostEmRecommendations) {
      setIsLoading(false);
    }
  }, [shouldFetchBoostEmInsights, boostEmRecommendations]);

  // update loading state if user does not have classes
  useEffect(() => {
    if (isClassesLoading || allClassesKeys.length) return;

    setIsLoading(false);
  }, [allClassesKeys, isClassesLoading]);

  return {
    boostEmRecommendations: currentBoostEmRecommendations,
    isLoading,
  };
};
